import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import { 
  Shield, 
  Eye, 
  Cookie, 
  Database, 
  Lock, 
  Mail, 
  Globe,
  UserCheck,
  Ban,
  Settings,
  FileText,
  ExternalLink,
  Download,
  Trash2,
  CheckCircle,
  AlertCircle,
  Loader2,
  ChevronRight,
  Menu,
  X,
  CreditCard,
} from 'lucide-react';
import { PrivacyToggle, usePrivacy } from '../components/PrivacyConsent';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Section definitions for TOC
const sections = [
  { id: 'summary', label: 'Quick Summary', icon: Eye },
  { id: 'settings', label: 'Your Settings', icon: Settings },
  { id: 'collected', label: 'Data We Collect', icon: Database },
  { id: 'third-party', label: 'Third-Party Services', icon: Globe },
  { id: 'advertising', label: 'Advertising', icon: Globe },
  { id: 'cookies', label: 'Cookies', icon: Cookie },
  { id: 'security', label: 'Data Security', icon: Lock },
  { id: 'rights', label: 'Your Rights', icon: UserCheck },
  { id: 'gdpr', label: 'GDPR (EU Users)', icon: Shield },
  { id: 'contact', label: 'Contact', icon: Mail },
];

const PrivacyPolicy = () => {
  const lastUpdated = "March 11, 2026";
  const { user, token } = useAuth();
  const { isEuUser } = usePrivacy();
  const [exportLoading, setExportLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [activeSection, setActiveSection] = useState('summary');
  const [showMobileToc, setShowMobileToc] = useState(false);
  const [openAccordions, setOpenAccordions] = useState(['summary', 'settings']);
  
  const sectionRefs = useRef({});

  // Intersection Observer for active section tracking
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveSection(entry.target.id);
          }
        });
      },
      { rootMargin: '-20% 0px -70% 0px' }
    );

    sections.forEach(({ id }) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });

    return () => observer.disconnect();
  }, []);

  const scrollToSection = (id) => {
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      // Ensure accordion is open
      if (!openAccordions.includes(id)) {
        setOpenAccordions([...openAccordions, id]);
      }
    }
    setShowMobileToc(false);
  };

  const handleExportData = async () => {
    if (!user) {
      toast.error('Please log in to export your data');
      return;
    }
    
    setExportLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/privacy/data-export`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) throw new Error('Export failed');
      
      const data = await response.json();
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `aadc-data-export-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      toast.success('Your data has been exported');
    } catch (error) {
      toast.error('Failed to export data. Please try again.');
    } finally {
      setExportLoading(false);
    }
  };

  const handleDeleteRequest = async () => {
    if (!user) {
      toast.error('Please log in to request deletion');
      return;
    }
    
    setDeleteLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/privacy/data`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) throw new Error('Deletion request failed');
      
      toast.success('Deletion request submitted. Your account will be deleted within 30 days.');
      setShowDeleteConfirm(false);
    } catch (error) {
      toast.error('Failed to submit deletion request. Please try again.');
    } finally {
      setDeleteLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background" data-testid="privacy-policy-page">
      {/* Header */}
      <div className="bg-gradient-to-b from-primary/5 to-background border-b">
        <div className="container mx-auto px-4 py-8">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 bg-primary/10 rounded-xl flex items-center justify-center">
              <Shield className="w-7 h-7 text-primary" />
            </div>
            <div>
              <h1 className="text-2xl font-bold">Privacy Policy</h1>
              <p className="text-sm text-muted-foreground">Last updated: {lastUpdated}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile TOC Toggle */}
      <div className="lg:hidden sticky top-0 z-40 bg-background/95 backdrop-blur border-b">
        <button
          onClick={() => setShowMobileToc(!showMobileToc)}
          className="w-full px-4 py-3 flex items-center justify-between text-sm font-medium"
        >
          <span className="flex items-center gap-2">
            <Menu className="w-4 h-4" />
            Jump to section
          </span>
          <ChevronRight className={`w-4 h-4 transition-transform ${showMobileToc ? 'rotate-90' : ''}`} />
        </button>
        
        {showMobileToc && (
          <div className="absolute top-full left-0 right-0 bg-background border-b shadow-lg max-h-[60vh] overflow-y-auto">
            {sections.map(({ id, label, icon: Icon }) => (
              <button
                key={id}
                onClick={() => scrollToSection(id)}
                className={`w-full px-4 py-3 flex items-center gap-3 text-sm hover:bg-muted transition-colors ${
                  activeSection === id ? 'bg-primary/10 text-primary font-medium' : ''
                }`}
              >
                <Icon className="w-4 h-4" />
                {label}
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="container mx-auto px-4 py-8">
        <div className="flex gap-8">
          {/* Sticky Sidebar TOC - Desktop */}
          <aside className="hidden lg:block w-64 flex-shrink-0">
            <div className="sticky top-8">
              <nav className="space-y-1">
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3 px-3">
                  Contents
                </p>
                {sections.map(({ id, label, icon: Icon }) => (
                  <button
                    key={id}
                    onClick={() => scrollToSection(id)}
                    className={`w-full px-3 py-2 rounded-lg flex items-center gap-3 text-sm transition-all ${
                      activeSection === id 
                        ? 'bg-primary/10 text-primary font-medium' 
                        : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                    {label}
                  </button>
                ))}
              </nav>

              {/* Quick Actions */}
              {user && (
                <div className="mt-8 p-4 bg-muted/50 rounded-lg">
                  <p className="text-xs font-semibold text-muted-foreground uppercase mb-3">Quick Actions</p>
                  <div className="space-y-2">
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="w-full gap-2 justify-start"
                      onClick={handleExportData}
                      disabled={exportLoading}
                    >
                      {exportLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                      Export Data
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </aside>

          {/* Main Content */}
          <main className="flex-1 max-w-3xl">
            <Accordion 
              type="multiple" 
              value={openAccordions}
              onValueChange={setOpenAccordions}
              className="space-y-4"
            >
              {/* Quick Summary */}
              <AccordionItem value="summary" id="summary" className="border rounded-lg px-4 bg-gradient-to-r from-green-50 to-emerald-50 border-green-200">
                <AccordionTrigger className="hover:no-underline py-4">
                  <div className="flex items-center gap-3">
                    <Eye className="w-5 h-5 text-green-600" />
                    <span className="font-semibold">The Short Version</span>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="pb-4">
                  <ul className="space-y-2 text-sm">
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                      <span><strong>Members see no Google ads</strong> - only guests see advertisements</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                      <span><strong>You control your privacy</strong> - choose your ad preferences anytime</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                      <span><strong>We don't sell your data</strong> - your information stays with us</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                      <span><strong>Minimal tracking</strong> - we collect only what's needed to run the site</span>
                    </li>
                  </ul>
                </AccordionContent>
              </AccordionItem>

              {/* Your Settings */}
              <AccordionItem value="settings" id="settings" className="border rounded-lg px-4">
                <AccordionTrigger className="hover:no-underline py-4">
                  <div className="flex items-center gap-3">
                    <Settings className="w-5 h-5 text-primary" />
                    <span className="font-semibold">Your Privacy Settings</span>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="pb-4">
                  <p className="text-sm text-muted-foreground mb-4">
                    Control how ads are displayed to you. These settings are stored locally and synced with our servers.
                  </p>
                  <PrivacyToggle />
                </AccordionContent>
              </AccordionItem>

              {/* Data We Collect */}
              <AccordionItem value="collected" id="collected" className="border rounded-lg px-4">
                <AccordionTrigger className="hover:no-underline py-4">
                  <div className="flex items-center gap-3">
                    <Database className="w-5 h-5 text-primary" />
                    <span className="font-semibold">Information We Collect</span>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="pb-4 space-y-4">
                  <div>
                    <h4 className="font-medium mb-1 text-sm">Account Information</h4>
                    <p className="text-sm text-muted-foreground">
                      Email, username, password (encrypted). Optional: location, bio, profile picture.
                    </p>
                  </div>
                  <div>
                    <h4 className="font-medium mb-1 text-sm">Pet Information</h4>
                    <p className="text-sm text-muted-foreground">
                      Names, breeds, photos, health records. Health records are encrypted and only accessible by you.
                    </p>
                  </div>
                  <div>
                    <h4 className="font-medium mb-1 text-sm">Usage Data</h4>
                    <p className="text-sm text-muted-foreground">
                      Anonymous statistics: pages visited, features used, country/region (no precise location).
                    </p>
                  </div>
                  <div>
                    <h4 className="font-medium mb-1 text-sm">Identity Verification (Optional)</h4>
                    <p className="text-sm text-muted-foreground">
                      If you choose to verify your identity, we use <strong>Stripe Identity</strong> to process a government-issued photo ID and a selfie. 
                      This involves <strong>biometric data</strong> (facial recognition) to match your selfie against your ID photo. 
                      Stripe processes this data under their own privacy policy — <strong>we never receive, store, or access your ID documents or biometric identifiers</strong>. 
                      We only receive a pass/fail verification result, your name, and date of birth as confirmed by Stripe.
                    </p>
                    <ul className="text-sm text-muted-foreground mt-2 space-y-1 ml-4 list-disc">
                      <li>Biometric identifiers are retained by Stripe for a maximum of <strong>1 year</strong>, then permanently deleted</li>
                      <li>You must give explicit consent before starting verification</li>
                      <li>You may revoke consent by contacting <a href="mailto:privacy@stripe.com" className="text-primary hover:underline">privacy@stripe.com</a></li>
                      <li>If Stripe Identity is unavailable, a fallback method uses a $0.50 card authorization (no biometric data involved)</li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-medium mb-1 text-sm">Payment Information</h4>
                    <p className="text-sm text-muted-foreground">
                      Membership payments are processed by <strong>Stripe</strong>. We do not store your credit card number, CVV, or full card details. 
                      Stripe handles all payment data under PCI DSS Level 1 compliance.
                    </p>
                  </div>
                </AccordionContent>
              </AccordionItem>

              {/* Third-Party Services */}
              <AccordionItem value="third-party" id="third-party" className="border rounded-lg px-4">
                <AccordionTrigger className="hover:no-underline py-4">
                  <div className="flex items-center gap-3">
                    <ExternalLink className="w-5 h-5 text-primary" />
                    <span className="font-semibold">Third-Party Data Processors</span>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="pb-4 space-y-4">
                  <p className="text-sm text-muted-foreground">
                    We use the following third-party services that may process your data. Each operates under their own privacy policy and data protection agreements.
                  </p>
                  <div className="grid gap-3">
                    <div className="p-3 bg-muted rounded-lg">
                      <h4 className="font-medium text-sm mb-1">Stripe (Payments & Identity Verification)</h4>
                      <p className="text-xs text-muted-foreground">
                        Processes membership payments and optional identity verification. Stripe is certified under the EU-U.S. Data Privacy Framework. 
                        For identity verification, Stripe may process biometric data (facial recognition from selfie + ID photo). 
                        Biometric identifiers are not shared with us and are deleted by Stripe within 1 year.
                      </p>
                      <a href="https://stripe.com/privacy" target="_blank" rel="noopener noreferrer" className="text-xs text-primary hover:underline mt-1 inline-flex items-center gap-1">
                        Stripe Privacy Policy <ExternalLink className="w-3 h-3" />
                      </a>
                    </div>
                    <div className="p-3 bg-muted rounded-lg">
                      <h4 className="font-medium text-sm mb-1">Google AdSense (Advertising)</h4>
                      <p className="text-xs text-muted-foreground">
                        Serves ads to non-premium members. May use cookies for ad personalization based on your consent settings. Premium members never see ads.
                      </p>
                      <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" className="text-xs text-primary hover:underline mt-1 inline-flex items-center gap-1">
                        Google Privacy Policy <ExternalLink className="w-3 h-3" />
                      </a>
                    </div>
                    <div className="p-3 bg-muted rounded-lg">
                      <h4 className="font-medium text-sm mb-1">Cloudflare Turnstile (Bot Protection)</h4>
                      <p className="text-xs text-muted-foreground">
                        Protects forms from automated abuse. Does not use cookies or collect personal data beyond what is needed for bot detection.
                      </p>
                      <a href="https://www.cloudflare.com/privacypolicy/" target="_blank" rel="noopener noreferrer" className="text-xs text-primary hover:underline mt-1 inline-flex items-center gap-1">
                        Cloudflare Privacy Policy <ExternalLink className="w-3 h-3" />
                      </a>
                    </div>
                    <div className="p-3 bg-muted rounded-lg">
                      <h4 className="font-medium text-sm mb-1">Resend (Email)</h4>
                      <p className="text-xs text-muted-foreground">
                        Sends transactional emails (verification, password reset, notifications). Only receives your email address for delivery purposes.
                      </p>
                    </div>
                    <div className="p-3 bg-muted rounded-lg">
                      <h4 className="font-medium text-sm mb-1">Embedded Media (YouTube, Vimeo, TikTok, etc.)</h4>
                      <p className="text-xs text-muted-foreground">
                        When you play a video embedded in a post, the video provider may set cookies and log your IP address, user-agent, and viewing session. We use privacy-enhanced embed modes where they exist (e.g. <code>youtube-nocookie.com</code>) and a <strong>click-to-load placeholder</strong> so no request is made to the video host until you choose to play. Once you click play, the third party&rsquo;s privacy policy applies.
                      </p>
                    </div>
                  </div>
                </AccordionContent>
              </AccordionItem>

              {/* External links and embeds */}
              <AccordionItem value="external-links" id="external-links" className="border rounded-lg px-4">
                <AccordionTrigger className="hover:no-underline py-4">
                  <div className="flex items-center gap-3">
                    <ExternalLink className="w-5 h-5 text-primary" />
                    <span className="font-semibold">External Links &amp; Embeds</span>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="pb-4 space-y-3">
                  <p className="text-sm text-muted-foreground">
                    AADC pages may contain links to external websites (partner organizations, news articles, donation pages, social media) and embedded third-party media. <strong>We have no control over the data-collection practices of those sites.</strong>
                  </p>
                  <ul className="text-sm text-muted-foreground list-disc pl-5 space-y-1">
                    <li><strong>Outbound links:</strong> Once you click through, the destination site can collect anything its operator chooses — cookies, IP, location, browser fingerprint, browsing history. AADC&rsquo;s privacy policy does not extend to those sites.</li>
                    <li><strong>Embedded videos:</strong> See the &ldquo;Embedded Media&rdquo; entry under Third-Party Data Processors. We do not auto-load video iframes; the user must click play, at which point the video host&rsquo;s privacy policy applies.</li>
                    <li><strong>Social previews:</strong> When external links are previewed inline, we fetch and cache the preview image &amp; metadata server-side so your browser does not directly contact the linked site for the preview.</li>
                  </ul>
                  <p className="text-sm text-muted-foreground">
                    For maximum control, browser-level privacy tools (uBlock Origin, Privacy Badger, Brave, Firefox containers) provide additional defenses beyond what any single website can offer.
                  </p>
                </AccordionContent>
              </AccordionItem>

              {/* Cost of verification */}
              <AccordionItem value="verification-cost" id="verification-cost" className="border rounded-lg px-4">
                <AccordionTrigger className="hover:no-underline py-4">
                  <div className="flex items-center gap-3">
                    <CreditCard className="w-5 h-5 text-primary" />
                    <span className="font-semibold">Why Identity Verification Has a Cost</span>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="pb-4 space-y-3">
                  <p className="text-sm text-muted-foreground">
                    Stripe charges <strong>AADC</strong> a processing fee (approximately $1.50 USD) for every identity verification check. That cost is passed through to the verification step — AADC does not profit from verification, we are paying Stripe.
                  </p>
                  <p className="text-sm text-muted-foreground">
                    In return, verified members receive expanded access to the Club&rsquo;s activities and website features (governance voting, club creation, marketplace participation, partner-program participation, opt-in member directories, premium content). The verification fee is the cost of maintaining a high-trust community where bots, AI agents, and duplicate accounts are excluded from privileged actions.
                  </p>
                </AccordionContent>
              </AccordionItem>

              {/* What we receive from Stripe vs what we don't */}
              <AccordionItem value="stripe-data-flow" id="stripe-data-flow" className="border rounded-lg px-4">
                <AccordionTrigger className="hover:no-underline py-4">
                  <div className="flex items-center gap-3">
                    <Lock className="w-5 h-5 text-primary" />
                    <span className="font-semibold">Data Received from Stripe (Verification)</span>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="pb-4 space-y-3">
                  <p className="text-sm text-muted-foreground">
                    When a member opts into identity verification, Stripe presents its own form, hosted on Stripe&rsquo;s domain. <strong>AADC does not see, log, or have access to anything submitted into that form</strong> &mdash; including ID document images, selfie images, address, ID number, or any biometric identifiers.
                  </p>
                  <p className="text-sm text-muted-foreground">
                    The data Stripe returns to AADC is limited to:
                  </p>
                  <ul className="text-sm text-muted-foreground list-disc pl-5 space-y-1">
                    <li>A pass / fail verification result</li>
                    <li>The first name and date of birth that match the verified ID</li>
                    <li>A Stripe verification reference ID (used solely to look up the verification status)</li>
                  </ul>
                  <p className="text-sm text-muted-foreground">
                    AADC does not share, sell, or otherwise transfer this received data to advertisers, data brokers, or analytics resellers. AADC reserves the right to publish <em>aggregate, de-identified group statistics</em> in the future (e.g., &ldquo;X members across Y states with Z average breeds per household&rdquo;); such aggregates contain no personally identifying information.
                  </p>
                </AccordionContent>
              </AccordionItem>

              {/* Advertising */}
              <AccordionItem value="advertising" id="advertising" className="border rounded-lg px-4">
                <AccordionTrigger className="hover:no-underline py-4">
                  <div className="flex items-center gap-3">
                    <Globe className="w-5 h-5 text-primary" />
                    <span className="font-semibold">Advertising & Tracking</span>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="pb-4 space-y-4">
                  <div className="p-3 bg-muted rounded-lg">
                    <h4 className="font-medium mb-2 text-sm">Who Sees Ads?</h4>
                    <ul className="text-sm text-muted-foreground space-y-1">
                      <li><strong>Guests:</strong> May see Google AdSense ads</li>
                      <li><strong>Founding Members (free tier):</strong> May see Google AdSense ads</li>
                      <li><strong>Charter Members (paid):</strong> Never see ads — an ad-free experience is part of the paid tier</li>
                    </ul>
                  </div>
                  
                  <div>
                    <h4 className="font-medium mb-2 text-sm">Your Ad Choices</h4>
                    <ul className="text-sm text-muted-foreground space-y-1">
                      <li>• <strong>Essential Only:</strong> No ads, no tracking</li>
                      <li>• <strong>Privacy-Friendly:</strong> Non-personalized ads</li>
                      <li>• <strong>Personalized:</strong> Standard Google ads</li>
                    </ul>
                  </div>

                  <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <h4 className="font-medium text-sm text-blue-800 mb-1">Consent Management (EEA / UK / Switzerland)</h4>
                    <p className="text-xs text-blue-700">
                      For visitors located in the European Economic Area, the United Kingdom, and Switzerland, we use{' '}
                      <a
                        href="https://support.google.com/fundingchoices/answer/9012850"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="underline"
                      >
                        Google&apos;s Funding Choices
                      </a>
                      , an IAB TCF v2.2-certified Consent Management Platform (CMP). The Funding Choices banner
                      appears before any advertising cookies are set and offers three clear choices — <strong>Consent</strong>,{' '}
                      <strong>Do not consent</strong>, and <strong>Manage options</strong>. Your response is stored per Google&apos;s
                      TCF signals and can be changed at any time by clicking the &quot;Privacy options&quot; link in the footer.
                      Ads only render after you have granted the appropriate level of consent.
                    </p>
                  </div>

                  <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg">
                    <Ban className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
                    <div>
                      <h4 className="font-medium text-sm text-red-800">What We Don&apos;t Allow</h4>
                      <p className="text-xs text-red-700 mt-1">
                        No invisible tracking pixels, fingerprinting scripts, or &quot;silent snoops&quot; beyond AdSense requirements.
                      </p>
                    </div>
                  </div>
                </AccordionContent>
              </AccordionItem>

              {/* Cookies */}
              <AccordionItem value="cookies" id="cookies" className="border rounded-lg px-4">
                <AccordionTrigger className="hover:no-underline py-4">
                  <div className="flex items-center gap-3">
                    <Cookie className="w-5 h-5 text-primary" />
                    <span className="font-semibold">Cookies We Use</span>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="pb-4">
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b">
                          <th className="text-left py-2 pr-4 font-medium">Cookie</th>
                          <th className="text-left py-2 pr-4 font-medium">Purpose</th>
                          <th className="text-left py-2 font-medium">Duration</th>
                        </tr>
                      </thead>
                      <tbody className="text-muted-foreground">
                        <tr className="border-b">
                          <td className="py-2 pr-4 font-mono text-xs">ff_beta_v3</td>
                          <td className="py-2 pr-4">Beta access</td>
                          <td className="py-2">Permanent</td>
                        </tr>
                        <tr className="border-b">
                          <td className="py-2 pr-4 font-mono text-xs">aadc_privacy_consent</td>
                          <td className="py-2 pr-4">Privacy preference</td>
                          <td className="py-2">12 months</td>
                        </tr>
                        <tr className="border-b">
                          <td className="py-2 pr-4 font-mono text-xs">auth_token</td>
                          <td className="py-2 pr-4">Login session</td>
                          <td className="py-2">7 days</td>
                        </tr>
                        <tr className="border-b">
                          <td className="py-2 pr-4 font-mono text-xs">Google AdSense*</td>
                          <td className="py-2 pr-4">Ad serving</td>
                          <td className="py-2">Varies</td>
                        </tr>
                        <tr>
                          <td className="py-2 pr-4 font-mono text-xs">PostHog*</td>
                          <td className="py-2 pr-4">Analytics</td>
                          <td className="py-2">1 year</td>
                        </tr>
                      </tbody>
                    </table>
                    <p className="text-xs text-muted-foreground mt-2">* Third-party - controlled by your privacy settings</p>
                  </div>
                </AccordionContent>
              </AccordionItem>

              {/* Security */}
              <AccordionItem value="security" id="security" className="border rounded-lg px-4">
                <AccordionTrigger className="hover:no-underline py-4">
                  <div className="flex items-center gap-3">
                    <Lock className="w-5 h-5 text-primary" />
                    <span className="font-semibold">How We Protect Your Data</span>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="pb-4">
                  <div className="grid gap-3">
                    <div className="p-3 bg-muted rounded-lg">
                      <h4 className="font-medium text-sm mb-1">Encryption</h4>
                      <p className="text-xs text-muted-foreground">HTTPS everywhere. Passwords hashed with bcrypt. Medical records encrypted at rest.</p>
                    </div>
                    <div className="p-3 bg-muted rounded-lg">
                      <h4 className="font-medium text-sm mb-1">Access Control</h4>
                      <p className="text-xs text-muted-foreground">Only you and authorized admins (for support) can access your data.</p>
                    </div>
                    <div className="p-3 bg-muted rounded-lg">
                      <h4 className="font-medium text-sm mb-1">Data Retention</h4>
                      <p className="text-xs text-muted-foreground">Retained while active. Request deletion anytime.</p>
                    </div>
                  </div>
                </AccordionContent>
              </AccordionItem>

              {/* Your Rights */}
              <AccordionItem value="rights" id="rights" className="border rounded-lg px-4">
                <AccordionTrigger className="hover:no-underline py-4">
                  <div className="flex items-center gap-3">
                    <UserCheck className="w-5 h-5 text-primary" />
                    <span className="font-semibold">Your Rights</span>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="pb-4">
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-4">
                    {[
                      { label: 'Access', desc: 'Get your data' },
                      { label: 'Correct', desc: 'Fix errors' },
                      { label: 'Delete', desc: 'Remove account' },
                      { label: 'Export', desc: 'Download data' },
                      { label: 'Opt-out', desc: 'Control ads' },
                      { label: 'Object', desc: 'Limit processing' },
                    ].map(({ label, desc }) => (
                      <div key={label} className="p-2 bg-muted rounded-lg text-center">
                        <div className="font-medium text-sm">{label}</div>
                        <div className="text-xs text-muted-foreground">{desc}</div>
                      </div>
                    ))}
                  </div>
                  
                  {user ? (
                    <div className="p-4 bg-primary/5 border border-primary/20 rounded-lg">
                      <h5 className="font-medium text-sm mb-3">Exercise Your Rights</h5>
                      <div className="flex flex-wrap gap-2">
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={handleExportData}
                          disabled={exportLoading}
                          className="gap-2"
                        >
                          {exportLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                          Export My Data
                        </Button>
                        
                        {!showDeleteConfirm ? (
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => setShowDeleteConfirm(true)}
                            className="gap-2 text-red-600 hover:text-red-700 hover:bg-red-50"
                          >
                            <Trash2 className="w-4 h-4" />
                            Delete Account
                          </Button>
                        ) : (
                          <div className="flex items-center gap-2 p-2 bg-red-50 border border-red-200 rounded-md">
                            <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0" />
                            <span className="text-xs text-red-700">Are you sure?</span>
                            <Button variant="destructive" size="sm" onClick={handleDeleteRequest} disabled={deleteLoading}>
                              {deleteLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Yes'}
                            </Button>
                            <Button variant="outline" size="sm" onClick={() => setShowDeleteConfirm(false)}>
                              Cancel
                            </Button>
                          </div>
                        )}
                      </div>
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground p-3 bg-muted rounded-lg">
                      <Link to="/login" className="text-primary hover:underline">Log in</Link> to export or delete your data.
                    </p>
                  )}
                </AccordionContent>
              </AccordionItem>

              {/* GDPR */}
              <AccordionItem value="gdpr" id="gdpr" className="border rounded-lg px-4">
                <AccordionTrigger className="hover:no-underline py-4">
                  <div className="flex items-center gap-3">
                    <Shield className="w-5 h-5 text-primary" />
                    <span className="font-semibold">GDPR (EU Users)</span>
                    {isEuUser && (
                      <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">You</span>
                    )}
                  </div>
                </AccordionTrigger>
                <AccordionContent className="pb-4 space-y-4">
                  <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                    <p className="font-medium text-green-800 text-sm flex items-center gap-2">
                      <CheckCircle className="w-4 h-4" />
                      GDPR Compliant
                    </p>
                    <p className="text-xs text-green-700 mt-1">
                      All tracking blocked until you consent. EU users auto-detected for stricter protections.
                    </p>
                  </div>
                  
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {[
                      { art: '15', right: 'Access', desc: 'Get your data' },
                      { art: '16', right: 'Rectification', desc: 'Correct errors' },
                      { art: '17', right: 'Erasure', desc: 'Be forgotten' },
                      { art: '20', right: 'Portability', desc: 'Export data' },
                      { art: '7', right: 'Withdraw', desc: 'Revoke consent' },
                      { art: '21', right: 'Object', desc: 'Stop processing' },
                    ].map(({ art, right, desc }) => (
                      <div key={art} className="p-2 bg-muted rounded-lg flex items-center gap-3">
                        <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center text-xs font-bold text-primary">
                          {art}
                        </div>
                        <div>
                          <div className="font-medium text-sm">{right}</div>
                          <div className="text-xs text-muted-foreground">{desc}</div>
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <h5 className="font-medium text-blue-800 text-sm mb-2">How We Comply</h5>
                    <ul className="text-xs text-blue-700 space-y-1">
                      <li>• Scripts blocked until explicit consent</li>
                      <li>• EU auto-detection for strict mode</li>
                      <li>• Advertising consent handled by Google&apos;s Funding Choices, an IAB TCF v2.2-certified CMP (3-choice: Consent / Do not consent / Manage options)</li>
                      <li>• Consent recorded with timestamps</li>
                      <li>• Auto-expires after 12 months</li>
                      <li>• Identity verification requires separate explicit consent before biometric processing</li>
                      <li>• Stripe is certified under EU-U.S. Data Privacy Framework for cross-border data transfers</li>
                      <li>• Biometric data processed by Stripe under Art. 9(2)(a) GDPR (explicit consent) and deleted within 1 year</li>
                    </ul>
                  </div>
                </AccordionContent>
              </AccordionItem>

              {/* Contact */}
              <AccordionItem value="contact" id="contact" className="border rounded-lg px-4">
                <AccordionTrigger className="hover:no-underline py-4">
                  <div className="flex items-center gap-3">
                    <Mail className="w-5 h-5 text-primary" />
                    <span className="font-semibold">Contact & Resources</span>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="pb-4 space-y-4">
                  <div>
                    <h4 className="font-medium text-sm mb-2">Privacy Questions</h4>
                    <a href="mailto:privacy@americandogclub.com" className="text-primary hover:underline text-sm">
                      privacy@americandogclub.com
                    </a>
                    <p className="text-xs text-muted-foreground mt-1">We respond within 30 days.</p>
                  </div>
                  
                  <div>
                    <h4 className="font-medium text-sm mb-2">External Resources</h4>
                    <ul className="space-y-1">
                      {[
                        { label: 'Stripe Privacy Policy', url: 'https://stripe.com/privacy' },
                        { label: 'Stripe Identity FAQ', url: 'https://support.stripe.com/questions/stripe-identity-faq' },
                        { label: 'Google Privacy Policy', url: 'https://policies.google.com/privacy' },
                        { label: 'Google Ads Settings', url: 'https://adssettings.google.com' },
                        { label: 'NAI Opt-Out', url: 'https://optout.networkadvertising.org' },
                      ].map(({ label, url }) => (
                        <li key={url}>
                          <a href={url} target="_blank" rel="noopener noreferrer" 
                             className="text-sm text-primary hover:underline flex items-center gap-1">
                            {label} <ExternalLink className="w-3 h-3" />
                          </a>
                        </li>
                      ))}
                    </ul>
                  </div>
                </AccordionContent>
              </AccordionItem>
            </Accordion>

            {/* Footer */}
            <div className="mt-8 pt-6 border-t text-center text-sm text-muted-foreground">
              <p>Effective as of {lastUpdated}</p>
              <Link to="/about">
                <Button variant="outline" size="sm" className="mt-4">
                  Back to About
                </Button>
              </Link>
            </div>
          </main>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
